import { Component, computed, inject, input } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { AbstractNewComponent } from '@unity/abstract/abstract-new.component';
import { AgreementsClient, CreatedResponseDto, DocumentCreateDto, DocumentsClient } from '@unity/clients';
import { TextareaControlComponent } from '@unity/components/forms/textarea-control/textarea-control.component';
import { TextboxReadonlyControlComponent } from '@unity/components/forms/textbox-readonly-control/textbox-readonly-control.component';
import { FileUploaderControlComponent } from '@unity/forms/file-uploader-control/file-uploader-control.component';
import { TextboxControlComponent } from '@unity/forms/textbox-control/textbox-control.component';
import { PageContainerComponent } from '@unity/layouts/page-container/page-container.component';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { CardNewSharedComponent } from '@unity/shared/composed/card-new-shared/card-new-shared.component';
import { nonZeroFormControl, optionalNumericFormControl, stringFormControl } from '@unity/utils/form-controls';
import { QuickActions } from '@unity/utils/quick-actions';
import { FormGroupControlSchema, UnityRouteUrlTrees } from '@unity/utils/types';

class DocumentCreateSchema implements FormGroupControlSchema<DocumentCreateDto> {
  public parent_document_id = optionalNumericFormControl();

  public agreement_id: FormControl<number>;
  public name = stringFormControl();
  public description = stringFormControl();
  public upload_id = nonZeroFormControl();

  public constructor(agreement_id: number) {
    this.agreement_id = nonZeroFormControl({ value: agreement_id });
  }
}

@Component({
  selector: 'unity-new-document-page',
  imports: [
    TextboxReadonlyControlComponent,
    TextareaControlComponent,
    FileUploaderControlComponent,
    TextboxControlComponent,
    PageContainerComponent,
    CardNewSharedComponent,
    ActionSharedComponent,
  ],
  templateUrl: './new-document-page.component.html',
  styleUrl: './new-document-page.component.scss',
})
export class NewDocumentPageComponent extends AbstractNewComponent<DocumentCreateDto, DocumentCreateSchema, CreatedResponseDto> {
  private readonly documentsClient = inject(DocumentsClient);
  private readonly agreementsClient = inject(AgreementsClient);

  public readonly $agreementId = input.required({ alias: 'agreementId', transform: (value: string) => parseInt(value) });
  public readonly $parentId = input(undefined, { alias: 'parentId', transform: (value: string) => parseInt(value) });

  protected override createResourceFn$ = (form: FormGroup<DocumentCreateSchema>) => {
    const formValues = form.getRawValue();
    return this.documentsClient.createOneForAgreement(formValues.agreement_id, formValues);
  };
  protected override redirectUrlFn = (): UnityRouteUrlTrees => ['/agreement', this.$agreementId(), 'document'];
  protected override newControlsFn(): DocumentCreateSchema {
    return new DocumentCreateSchema(0);
  }

  protected readonly $hasParent = computed(() => this.$parentId() !== 0);
  protected readonly $workingName = computed(() => (this.$hasParent() === true ? 'Amendment' : 'Document'));
  protected readonly $nameFieldDescription = computed(() => `Provide the ${this.$workingName()} name.`);
  protected readonly $descriptionFieldDescription = computed(() => `Provide a description of the ${this.$workingName()}.`);

  protected agreementResource = rxResource({
    params: () => {
      const agreementId = this.$agreementId();
      return agreementId ? { agreementId } : undefined;
    },
    stream: ({ params: { agreementId } }) => this.agreementsClient.getOneAsMini(agreementId),
  });

  protected parentDocumentResource = rxResource({
    params: () => {
      const agreementId = this.$agreementId();
      const parentId = this.$parentId();
      return agreementId && parentId ? { agreementId, parentId } : undefined;
    },
    stream: ({ params: { agreementId, parentId } }) => this.documentsClient.getOneForAgreementAsMini(agreementId, parentId),
  });

  protected override onSubmit(): void {
    const formGroup = this.$formGroup();
    const agreement = this.agreementResource.value();
    const parentDocument = this.parentDocumentResource.value();
    if (!agreement) {
      throw new Error('Unable to find an agreement to attach this document to.');
    }

    formGroup.controls.agreement_id.patchValue(agreement.id);
    if (parentDocument) {
      formGroup.controls.parent_document_id.patchValue(parentDocument.id);
    }
    super.onSubmit();
  }

  protected override readonly $contentHeader = computed(() => {
    const parentDocument = this.parentDocumentResource.value();
    return parentDocument ? 'Amend Document' : 'Create New Document';
  });

  protected readonly $contentHeaderSecondary = computed(() => {
    const agreement = this.agreementResource.value();
    return `Agreement: ${agreement?.name}`;
  });

  protected readonly $returnToAgreementAction = computed(() => QuickActions.ReturnToAgreement(this.$agreementId()));
}
